import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import projectService from '../api/projectService';
import { useQueryConfig } from './useQueryConfig';

/**
 * Custom hook for Projects API operations
 */
export const useProjects = () => {
  const queryClient = useQueryClient();
  const { queryKeys } = useQueryConfig();

  // Query to fetch all projects
  const useAllProjects = () => {
    return useQuery({
      queryKey: queryKeys.projects.all,
      queryFn: projectService.getAllProjects,
    });
  };

  // Query to fetch a specific project
  const useProject = (id) => {
    return useQuery({
      queryKey: queryKeys.projects.detail(id),
      queryFn: () => projectService.getProject(id),
      enabled: !!id,
    });
  };

  // Mutation to create a new project
  const useCreateProject = () => {
    console.log("Use Create Project: ");
    return useMutation({
      mutationFn: projectService.createProject,
      onSuccess: () => {
        // Invalidate the projects list query to refetch data
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      },
    });
  };

  // Mutation to duplicate a project
  const useDuplicateProject = () => {
    return useMutation({
      mutationFn: ({ projectId, data }) => projectService.duplicateProject(projectId, data),
      onSuccess: () => {
        // Invalidate the projects list query to refetch data
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      },
    });
  };

  // Mutation to update a project
  const useUpdateProject = () => {
    return useMutation({
      mutationFn: ({ id, data }) => projectService.updateProject(id, data),
      onSuccess: (_, variables) => {
        // Invalidate both the list and the specific project query
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.detail(variables.id),
        });
      },
    });
  };

  // Mutation to delete a project
  const useDeleteProject = () => {
    return useMutation({
      mutationFn: projectService.deleteProject,
      onSuccess: () => {
        // Invalidate the projects list query
        queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      },
    });
  };

  // Query to get API key for a project
  const useApiKey = (id) => {
    return useQuery({
      queryKey: queryKeys.projects.apiKey(id),
      queryFn: () => projectService.getApiKey(id),
      enabled: !!id,
      // API keys are sensitive, so don't keep them in cache for too long
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 10, // 10 minutes
    });
  };

  // Mutation to regenerate API key
  const useRegenerateApiKey = () => {
    return useMutation({
      mutationFn: projectService.regenerateApiKey,
      onSuccess: (_, id) => {
        // Invalidate the API key query to refetch with the new key
        queryClient.invalidateQueries({
          queryKey: queryKeys.projects.apiKey(id),
        });
      },
    });
  };

  // Mutation for generating a prompt with AI
  const useGeneratePrompt = () => {
    return useMutation({
      mutationFn: ({ projectId, promptData }) => 
        projectService.generatePrompt(projectId, promptData),
    });
  };

  return {
    useAllProjects,
    useProject,
    useCreateProject,
    useDuplicateProject,
    useUpdateProject,
    useDeleteProject,
    useApiKey,
    useRegenerateApiKey,
    useGeneratePrompt,
  };
};

export default useProjects; 